import { Bot } from 'lucide-react'
import type * as React from 'react'

import { Badge, type BadgeProps, Card } from '@/components/ui/surface'
import { personTone } from '@/lib/person'
import { type RecordScope, viaPath } from '@/lib/types'
import { cn, EMPTY, formatDateTime, initials, relativeTime } from '@/lib/utils'

export function Avatar({
  name,
  email,
  src,
  className,
}: {
  name: string
  email?: string
  src?: string | null
  className?: string
}) {
  // design.md §2 People: the person's own colour, as on a calendar.
  const base =
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ' +
    `${personTone(name || email || '')} text-[11px] font-semibold select-none`
  if (src) {
    return (
      <img src={src} alt="" className={cn(base, 'size-6 object-cover', className)} />
    )
  }
  return (
    <span className={cn(base, 'size-6', className)} aria-hidden>
      {initials(name, email)}
    </span>
  )
}

/** design.md §4 UserCell: avatar + name + email. */
export function UserCell({
  name,
  email,
  src,
}: {
  name: string
  email: string
  src?: string | null
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar name={name} email={email} src={src} />
      <div className="min-w-0">
        <p className="truncate text-[13.5px] text-text">{name || email.split('@')[0]}</p>
        <p className="truncate text-[11px] text-faint">{email}</p>
      </div>
    </div>
  )
}

/** design.md §4: short relative label, exact value on hover. */
export function RelativeTime({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-faint">{EMPTY}</span>
  return (
    <time
      dateTime={value}
      title={formatDateTime(value) ?? undefined}
      className="tabular whitespace-nowrap text-muted"
    >
      {relativeTime(value)}
    </time>
  )
}

const SCOPE_TONE: Record<string, BadgeProps['tone']> = {
  all: 'info',
  team: 'accent',
  own: 'warn',
  none: 'neutral',
}

const SCOPE_LABEL: Record<string, string> = {
  all: 'All records',
  team: 'Team records',
  own: 'Own records',
  none: 'No records',
}

/** "Own records", "Through institution", "Own records · every status prospect". */
export function scopeLabel(scope: RecordScope): string {
  const text = scope
    .split('+')
    .map((part) => {
      const path = viaPath(part)
      if (path) return `through ${path}`
      // where:status=prospect|trial — every record holding one of those values.
      // from:sales.opportunity.institution — linked from one's own opportunities.
      if (part.startsWith('from:')) {
        const table = part.slice('from:'.length).split('.')[1] ?? ''
        return `linked from ${table.replace(/_/g, ' ')}s`
      }
      if (part.startsWith('where:')) {
        const [field, values = ''] = part.slice('where:'.length).split('=')
        return `every ${field.replace(/_/g, ' ')} ${values.split('|').join(' or ')}`
      }
      return SCOPE_LABEL[part] ?? part
    })
    .join(' · ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function ScopePill({ scope }: { scope: RecordScope }) {
  const first = scope.split('+')[0]
  return (
    <Badge tone={SCOPE_TONE[first] ?? 'accent'} className="font-normal">
      {scopeLabel(scope)}
    </Badge>
  )
}

/** Marks agent-authored audit entries (design.md §P10). */
export function ActorBadge({ kind }: { kind: 'user' | 'agent' | 'system' }) {
  if (kind === 'user') return null
  return (
    <Badge tone={kind === 'agent' ? 'accent' : 'neutral'} className="gap-1">
      {kind === 'agent' ? <Bot className="size-3" /> : null}
      {kind === 'agent' ? 'Agent' : 'System'}
    </Badge>
  )
}

/** design.md §5: an absent value is an em dash in faint, never a blank. */
export function Empty() {
  return <span className="text-faint">{EMPTY}</span>
}

export function PageHeader({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 pb-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
      </div>
      {children ? <div className="flex items-center gap-2">{children}</div> : null}
    </div>
  )
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY
  if (Array.isArray(value)) return value.length ? value.join(', ') : EMPTY
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * design.md §4/§P10 DiffView: changed fields only, before in danger-soft, after in
 * success-soft, unchanged collapsed. Carries +/- as well as colour (§7).
 */
export function DiffView({
  before,
  after,
}: {
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
}) {
  const keys = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]))
  const changed = keys.filter((key) => renderValue(before?.[key]) !== renderValue(after?.[key]))
  const unchanged = keys.length - changed.length

  if (keys.length === 0) {
    return <p className="text-xs text-faint">No field-level detail recorded.</p>
  }

  return (
    <div className="space-y-2">
      {changed.length === 0 ? (
        <p className="text-xs text-faint">No fields changed.</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border-soft">
          {changed.map((key) => (
            <div
              key={key}
              className="grid grid-cols-[9rem_1fr] items-start gap-x-3 border-b border-border-soft px-3 py-2 text-xs last:border-0"
            >
              <span className="truncate pt-0.5 font-medium text-muted" title={key}>
                {key}
              </span>
              <div className="min-w-0 space-y-1">
                {before ? (
                  <p className="flex gap-1.5 rounded bg-danger-soft px-1.5 py-0.5 font-mono break-all text-danger">
                    <span aria-hidden>−</span>
                    <span className="sr-only">before</span>
                    {renderValue(before[key])}
                  </p>
                ) : null}
                {after ? (
                  <p className="flex gap-1.5 rounded bg-success-soft px-1.5 py-0.5 font-mono break-all text-success">
                    <span aria-hidden>+</span>
                    <span className="sr-only">after</span>
                    {renderValue(after[key])}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
      {unchanged > 0 ? (
        <details className="text-xs text-faint">
          <summary className="cursor-pointer select-none hover:text-muted">
            {unchanged} unchanged field{unchanged === 1 ? '' : 's'}
          </summary>
          <dl className="mt-1.5 grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1 px-3">
            {keys
              .filter((key) => !changed.includes(key))
              .map((key) => (
                <div key={key} className="col-span-2 grid grid-cols-subgrid">
                  <dt className="truncate font-medium">{key}</dt>
                  <dd className="truncate font-mono">{renderValue(after?.[key])}</dd>
                </div>
              ))}
          </dl>
        </details>
      ) : null}
    </div>
  )
}

/** Chips rather than a select: three or four options, all worth seeing at once. */
export function Chips<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
  label: string
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const on = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-md border px-2.5 py-1 text-xs transition-colors',
              on
                ? 'border-accent bg-accent-soft font-medium text-accent'
                : 'border-border text-muted hover:text-text',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** A headline figure: what it is, the number, and a line of context under it. */
export function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string
  value: string
  note?: React.ReactNode
  /** A figure past its limit says so in words too (the note), never by colour alone. */
  tone?: 'warn' | 'danger'
}) {
  return (
    <Card className="px-4 py-3">
      <p className="text-[11px] text-muted">{label}</p>
      <p
        className={cn(
          'tabular mt-1 text-xl font-semibold',
          tone === 'warn' && 'text-warn',
          tone === 'danger' && 'text-danger',
        )}
      >
        {value}
      </p>
      {note ? <p className="mt-0.5 text-[11px] text-faint">{note}</p> : null}
    </Card>
  )
}
